import { useState, type ReactNode } from "react";
import {
  ActionIcon,
  Badge,
  Box,
  Card,
  Collapse,
  CopyButton,
  Group,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
  Code,
  rem,
  useMantineTheme,
} from "@mantine/core";
import {
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconCopy,
} from "@tabler/icons-react";
import { useMediaQuery } from "@mantine/hooks";

import type { ApiEndpoint } from "../apiEndpoints";

interface InfoBlockProps {
  label: string;
  children: ReactNode;
}

const InfoBlock = ({ label, children }: InfoBlockProps) => {
  const theme = useMantineTheme();

  return (
    <Box style={{ minWidth: 0 }}>
      <Text
        fw={600}
        mb="xs"
        c={theme.colors.plain_text[0]}>
        {label}
      </Text>
      {children}
    </Box>
  );
};

export const EndpointCard = ({
  title,
  method,
  endpoint,
  description,
  icon: Icon,
  payload,
  curlExample,
  headers,
  parameters,
}: ApiEndpoint) => {
  const theme = useMantineTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const iconColor = theme.colors.R500?.[0] ?? theme.colors.blue[6];
  const isMobile = useMediaQuery("(max-width: 48em)");

  return (
    <Card
      shadow="sm"
      padding="lg"
      radius="md"
      withBorder
      style={{ minWidth: 0 }}>
      <Stack
        gap="md"
        style={{ minWidth: 0 }}>
        <Group
          justify="space-between"
          style={{ cursor: "pointer", minWidth: 0 }}
          onClick={() => setIsExpanded((current) => !current)}>
          <Group
            wrap="nowrap"
            gap="sm"
            style={{ minWidth: 0 }}>
            <Icon
              size={24}
              color={iconColor}
            />
            <Title
              order={3}
              c={theme.colors.plain_text[0]}>
              {title}
            </Title>
          </Group>
          <Group gap="sm">
            <Badge
              color="grape"
              variant="light">
              {method}
            </Badge>
            {isExpanded ? (
              <IconChevronUp size={20} />
            ) : (
              <IconChevronDown size={20} />
            )}
          </Group>
        </Group>

        <Text
          c={theme.colors.plain_text[0]}
          size="sm">
          {description}
        </Text>

        <InfoBlock label="Endpoint:">
          <Code
            block
            style={{
              overflowX: "auto",
              width: "100%",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}>
            {endpoint}
          </Code>
        </InfoBlock>

        <Collapse
          in={isExpanded}
          transitionDuration={200}>
          <Stack
            gap="md"
            mt="md"
            style={{ minWidth: 0 }}>
            {parameters && parameters.length > 0 && (
              <InfoBlock label="Parâmetros:">
                {isMobile ? (
                  <Stack gap="sm">
                    {parameters.map((param) => (
                      <Card
                        key={param.name}
                        withBorder
                        padding="sm"
                        radius="md">
                        <Stack gap={4}>
                          <Group
                            justify="space-between"
                            gap="xs"
                            wrap="nowrap">
                            <Code>{param.name}</Code>
                            <Badge
                              size="sm"
                              color="blue"
                              variant="light">
                              {param.type}
                            </Badge>
                          </Group>
                          <Text size="sm">{param.description}</Text>
                          {param.values && param.values.length > 0 && (
                            <Text
                              size="xs"
                              c="dimmed">
                              Valores:{" "}
                              {param.values
                                .map((value) => `"${value}"`)
                                .join(", ")}
                            </Text>
                          )}
                        </Stack>
                      </Card>
                    ))}
                  </Stack>
                ) : (
                  <Box
                    style={{ overflowX: "auto", width: "100%", minWidth: 0 }}>
                    <Table
                      withTableBorder
                      withColumnBorders
                      style={{ minWidth: rem(560) }}>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Nome</Table.Th>
                          <Table.Th>Tipo</Table.Th>
                          <Table.Th>Descrição</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {parameters.map((param) => (
                          <Table.Tr key={param.name}>
                            <Table.Td>
                              <Code>{param.name}</Code>
                            </Table.Td>
                            <Table.Td>
                              <Badge
                                size="sm"
                                color="blue"
                                variant="light">
                                {param.type}
                              </Badge>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">
                                {param.description}
                                {param.values && (
                                  <Text
                                    size="xs"
                                    c="dimmed"
                                    mt={4}>
                                    Valores:{" "}
                                    {param.values
                                      .map((value) => `"${value}"`)
                                      .join(", ")}
                                  </Text>
                                )}
                              </Text>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </Box>
                )}
              </InfoBlock>
            )}

            <InfoBlock label="Headers:">
              <Code
                block
                style={{
                  overflowX: "auto",
                  width: "100%",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}>
                {JSON.stringify(headers, null, 2)}
              </Code>
            </InfoBlock>

            <InfoBlock label="Payload Example:">
              <Code
                block
                style={{
                  overflowX: "auto",
                  width: "100%",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}>
                {JSON.stringify(payload, null, 2)}
              </Code>
            </InfoBlock>

            <InfoBlock label="cURL Example:">
              <Group
                justify="space-between"
                align="flex-start"
                wrap="nowrap"
                style={{ minWidth: 0 }}>
                <Text
                  fw={600}
                  c={theme.colors.plain_text[0]}
                  size="sm">
                  Copie o exemplo e ajuste com os seus dados:
                </Text>
                <CopyButton
                  value={curlExample}
                  timeout={2000}>
                  {({ copied, copy }) => (
                    <Tooltip
                      label={copied ? "Copiado" : "Copiar"}
                      withArrow
                      position="left">
                      <ActionIcon
                        color={copied ? "teal" : "gray"}
                        variant="subtle"
                        onClick={(event) => {
                          event.stopPropagation();
                          copy();
                        }}>
                        {copied ? (
                          <IconCheck size={16} />
                        ) : (
                          <IconCopy size={16} />
                        )}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>
              </Group>
              <Code
                block
                style={{
                  fontSize: rem(12),
                  overflowX: "auto",
                  width: "100%",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}>
                {curlExample}
              </Code>
            </InfoBlock>
          </Stack>
        </Collapse>
      </Stack>
    </Card>
  );
};
